import type { BatchStatus as BatchRetryStatus } from '../types/batch-retry.api.types';
import type { BatchStatus } from '../types/catalog-operations.types';

/** What the status chip shows for a batch whose server-reconciled `batchStatus` is present. */
export interface BatchStatusDisplay {
  /** Base chip style (`StatusChip`'s own palette). */
  status: BatchStatus;
  label: string;
  /** Tone override layered over the base style (tailwind-merge resolves the clash). */
  className?: string;
}

interface BatchStatusDisplayInput {
  batchStatus: BatchRetryStatus | null | undefined;
  /** The row's existing UI status — decides "Delivered" vs "Completed" wording. */
  fallbackStatus: BatchStatus;
  retryAttempts?: number | null | undefined;
}

const MUTED_TONE = 'bg-muted text-muted-foreground';
const WARNING_TONE = 'bg-warning-50 text-warning';

/**
 * Maps the reconciled `batchStatus` (root run + retry children) to a chip label and tone.
 * Returns `null` when the backend predates the field, so callers keep their existing logic.
 */
export function resolveBatchStatusDisplay({
  batchStatus,
  fallbackStatus,
  retryAttempts,
}: BatchStatusDisplayInput): BatchStatusDisplay | null {
  if (!batchStatus) return null;
  const delivered = fallbackStatus === 'delivered';
  switch (batchStatus) {
    case 'IN_PROGRESS':
      return { status: 'in-progress', label: 'In Progress' };
    case 'PAUSED':
      return { status: 'in-progress', label: 'Paused', className: MUTED_TONE };
    case 'RETRYING':
      return {
        status: 'in-progress',
        label:
          retryAttempts && retryAttempts > 0 ? `Retrying · #${String(retryAttempts)}` : 'Retrying',
      };
    case 'AWAITING_APPROVAL':
      return { status: 'awaiting-approval', label: 'Review' };
    case 'DELIVERING':
      return { status: 'delivering', label: 'Delivering' };
    case 'COMPLETED':
      return {
        status: delivered ? 'delivered' : 'completed',
        label: delivered ? 'Delivered' : 'Completed',
      };
    case 'COMPLETED_WITH_ISSUES':
      return {
        status: delivered ? 'delivered' : 'completed',
        label: delivered ? 'Delivered · issues' : 'Completed · issues',
        className: WARNING_TONE,
      };
    case 'FAILED':
      return { status: 'failed', label: 'Failed' };
    case 'REJECTED':
      return { status: 'rejected', label: 'Rejected' };
  }
}

/** "n child runs" / "no child runs" sub-line under the grid status chip. */
export function describeChildRuns(retryAttempts: number | null | undefined): string {
  const n = retryAttempts ?? 0;
  if (n <= 0) return 'no child runs';
  return `${String(n)} child run${n === 1 ? '' : 's'}`;
}
